// Sidebar del portal Sales (Vendedor) — fuente: mockup portal-vendedor.html (variante con grupo
// "Admin de tienda" de portal-vendedor-tienda.html). «Nueva venta» agrupa las pantallas que arrancan una venta.
//
// RBAC-driven: cada item declara su `permission`; el AppShell lo esconde si el usuario no lo tiene
// (patrón Fase 2, como fiado-agents-webapp). El KYC no se ofrece aquí: lo resuelve Cumplimiento en
// `/retail/kyc-review` del backoffice, y su permiso es de alcance TENANT.
import { linkOptions } from '@tanstack/react-router';
import {
    Banknote,
    BookOpen,
    Calculator,
    CreditCard,
    HandCoins,
    Headphones,
    Home,
    Smartphone,
    Store,
    User,
    UserPlus,
    Wallet,
} from 'lucide-react';
import type { AppShellNavGroup } from '@/shared/patterns/AppShell';

export const SALES_PORTAL_TITLE = 'Portal Vendedor';

/** Crumb del topbar de las pantallas del grupo «Nueva venta», como en el mock. */
const NEW_SALE_CRUMB = 'Portal Vendedor · Nueva venta';

const section = (slug: string) => linkOptions({ to: '/sales/$section', params: { section: slug } });

export const salesNav: AppShellNavGroup[] = [
    {
        items: [
            {
                label: 'Home',
                icon: Home,
                link: linkOptions({ to: '/sales' }),
                exact: true,
                permission: 'retail.seller.home.read',
                status: 'connected',
            },
        ],
    },
    {
        label: 'Nueva venta',
        items: [
            {
                slug: 'phone-sale',
                label: 'Teléfono',
                icon: Smartphone,
                link: section('phone-sale'),
                pageTitle: 'Venta de teléfonos',
                crumb: NEW_SALE_CRUMB,
                permission: 'retail.wizard.operate',
                status: 'connected',
            },
            {
                slug: 'accessories',
                label: 'Venta rápida',
                icon: Headphones,
                link: section('accessories'),
                pageTitle: 'Venta rápida',
                crumb: NEW_SALE_CRUMB,
                permission: 'retail.wizard.operate',
                status: 'connected',
            },
            {
                // Apertura de cuenta sin venta: el wizard en modo apertura (D-01).
                label: 'Abrir cuenta',
                icon: UserPlus,
                link: linkOptions({ to: '/wizard', search: { accountOpening: true } }),
                permission: 'retail.wizard.operate',
                status: 'connected',
            },
            {
                slug: 'collections',
                label: 'Cobro',
                icon: Banknote,
                link: section('collections'),
                pageTitle: 'Cobros de crédito',
                crumb: NEW_SALE_CRUMB,
                permission: 'loan.credit.collection.read.own',
                status: 'connected',
            },
            {
                slug: 'quoter',
                label: 'Cotizador',
                icon: Calculator,
                link: section('quoter'),
                pageTitle: 'Cotizador de créditos',
                crumb: NEW_SALE_CRUMB,
                permission: 'retail.wizard.operate',
                status: 'connected',
            },
        ],
    },
    {
        // «Prospectos» es pestaña de Clientes (`?tab=prospects`); «Teléfonos» sigue solo por URL.
        label: 'Mi día',
        items: [
            {
                slug: 'my-sales',
                label: 'Ventas',
                icon: CreditCard,
                link: section('my-sales'),
                permission: 'retail.sale.read',
                status: 'connected',
            },
            {
                slug: 'customers',
                label: 'Clientes',
                icon: User,
                link: section('customers'),
                permission: 'retail.customer.search',
                status: 'connected',
            },
            {
                slug: 'catalog',
                label: 'Catálogo',
                icon: BookOpen,
                link: section('catalog'),
                permission: 'retail.wizard.operate',
                status: 'connected',
            },
            {
                // Enganches por regresar de ventas a crédito canceladas en la tienda.
                slug: 'refunds',
                label: 'Reembolsos',
                icon: HandCoins,
                link: section('refunds'),
                pageTitle: 'Reembolsos de enganche',
                permission: 'retail.sale.read',
                status: 'connected',
            },
        ],
    },
    {
        label: 'Mi progreso',
        items: [
            {
                slug: 'my-commissions',
                label: 'Comisiones',
                icon: Wallet,
                link: section('my-commissions'),
                permission: 'retail.commission.own.read',
                status: 'connected',
            },
        ],
    },
    {
        label: 'Admin de tienda',
        // "Mi tienda" es del ENCARGADO: se gatea con el mismo permiso que exige su endpoint
        // `GET /seller/store-summary`, así que a quien no lo tiene el ítem no le aparece.
        items: [
            {
                slug: 'my-store',
                label: 'Mi tienda',
                icon: Store,
                link: section('my-store'),
                permission: 'retail.store.summary.read',
                status: 'connected',
            },
        ],
    },
];
